import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import {
	AI_SECTION,
	AI_SECTION_FULL_PAGE_PRIMARY_TOOLBAR,
	AI_SECTION_COMMENT_PRIMARY_TOOLBAR,
	AI_SECTION_PRIMARY_TOOLBAR,
	AI_SUGGESTIONS_GROUP,
	AI_SUGGESTIONS_BUTTON,
	AI_SECTION_RANK,
	AI_SUGGESTIONS_GROUP_RANK,
	ROVO_GROUP_DISPLAY_SMALL,
	ROVO_GROUP_DISPLAY_SMALL_RANK,
} from '@atlaskit/editor-common/toolbar';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { Show, ToolbarButton, ToolbarButtonGroup } from '@atlaskit/editor-toolbar';
import type { RegisterComponent } from '@atlaskit/editor-toolbar-model';
import SuggestedEditIcon from '@atlaskit/icon-lab/core/suggested-edit';

import type { AiSuggestionsPlugin } from '../../aiSuggestionsPluginType';

const AiSuggestionsButton = ({
	api,
}: {
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const label = formatMessage(aiSuggestionsMessages.suggestEditsButtonLabel);

	const handleClick = useCallback(() => {
		api?.aiSuggestions?.actions.startThinking({ entryPoint: 'primaryToolbar' });
	}, [api]);

	return (
		<ToolbarButton
			onClick={handleClick}
			testId="editor-toolbar__ai-suggestions-button"
			iconBefore={<SuggestedEditIcon label={label} size="small" />}
			ignoreInteractionDisabled
		>
			{label}
		</ToolbarButton>
	);
};

export const AISuggestionsToolbarComponents = ({
	api,
}: {
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined;
}): RegisterComponent[] => {
	return [
		// AI SUGGESTIONS GROUP — only registered in the pinned primary toolbar sections,
		// not in AI_SECTION (which is used by the floating toolbar).
		//
		// The group wraps its children in <Show above="lg"> so it hides below the breakpoint
		{
			type: AI_SUGGESTIONS_GROUP.type,
			key: AI_SUGGESTIONS_GROUP.key,
			parents: [
				{
					type: AI_SECTION.type,
					key: AI_SECTION_FULL_PAGE_PRIMARY_TOOLBAR.key,
					rank: AI_SECTION_RANK[AI_SUGGESTIONS_GROUP.key],
				},
				{
					type: AI_SECTION.type,
					key: AI_SECTION_COMMENT_PRIMARY_TOOLBAR.key,
					rank: AI_SECTION_RANK[AI_SUGGESTIONS_GROUP.key],
				},
				{
					type: AI_SECTION_PRIMARY_TOOLBAR.type,
					key: AI_SECTION_PRIMARY_TOOLBAR.key,
					rank: AI_SECTION_RANK[AI_SUGGESTIONS_GROUP.key],
				},
			],
			component: ({ children }: { children: React.ReactNode }) => (
				<Show above="lg">
					<ToolbarButtonGroup>{children}</ToolbarButtonGroup>
				</Show>
			),
		},
		// AI SUGGESTIONS BUTTON — shown as a full labelled button when space is available
		// (via AI_SUGGESTIONS_GROUP above "lg"), or as an icon-only button with tooltip
		// inside ROVO_GROUP_DISPLAY_SMALL when space is tight (below "lg").
		{
			type: AI_SUGGESTIONS_BUTTON.type,
			key: AI_SUGGESTIONS_BUTTON.key,
			parents: [
				{
					type: AI_SUGGESTIONS_GROUP.type,
					key: AI_SUGGESTIONS_GROUP.key,
					rank: AI_SUGGESTIONS_GROUP_RANK[AI_SUGGESTIONS_BUTTON.key],
				},
				{
					type: ROVO_GROUP_DISPLAY_SMALL.type,
					key: ROVO_GROUP_DISPLAY_SMALL.key,
					rank: ROVO_GROUP_DISPLAY_SMALL_RANK[AI_SUGGESTIONS_BUTTON.key],
				},
			],
			component: () => <AiSuggestionsButton api={api} />,
		},
	];
};
